import React, { useState, useEffect, useContext, useMemo } from "react";

// Components //
import ShareModal from "../../molecules/share-modal/shareModal";
import JobName from "../../../components/molecules/job-name/JobName";
import ModalGuiaCdc from "../../molecules/modal-guia-cdc/modalGuiaCdc";
import Breadcrumb from "../../../components/atoms/breadcrumb/breadcrumb";
import GoBackLink from "../../../components/atoms/go-back-link/GoBackLink";
import JobTipsPostsCarousel from "../../molecules/job-tips-carousel/JobTipsCarousel";
import JobSuggestions from "../../../components/molecules/job-suggestions/JobSuggestions";
import JobGeneralInfo from "../../../components/molecules/job-generalinfo/JobGeneralInfo";
import JobRecommendations from "../../../components/molecules/job-recommendations/JobRecommendations";
import JobRecommendedJobsCarousel from "../../molecules/job-recommended-carousel/JobRecommendedCarousel";

// Services //
import { getJobs } from "../../../services/vacante.services";
import type { JobDetails, JobsFilters } from "../../../interfaces/JobDetails";
import { useJobRecommendationTips } from "./query";
import GlobalContext from "../../../contexts/globalContext";
import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import useBlockScroll from "../../../hooks/useBlockScroll";
import useElementSize from "../../../hooks/useElementSize";
import { useMediaQuery } from "@mui/material";

// Styles //
import "./JobDetails.scss";

const MAIN_CONTAINER_CLASSNAME = "o-job-details";

const breadcrumbItems: {
    title: string;
    url?: string;
}[] = [
    {
        title: "Inicio",
        url: "/servicio-de-empleo/personas",
    },
    {
        title: "Vacantes",
        url: "/servicio-de-empleo/personas/vacantes-en-antioquia",
    },
];

const similarJobsQuantity = 10;

const recommendedJobsQuantity = 3;

const HEADER_SELECTOR = ".m-header";

const FALLBACK_HEADER_HEIGHT = 90;

interface JobDetailsProps {
    jobDetails: JobDetails;
}

const JobDetailsScreen = ({ jobDetails }: JobDetailsProps) => {
    const isMobile = useMediaQuery('(max-width:960px)');

    const globalContext = useContext(GlobalContext);

    const { isBrowser, window } = useBrowserMode();

    const headerSize = useElementSize(HEADER_SELECTOR);

    const [similarPositions, setSimilarPositions] = useState<JobDetails[]>([]);

    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

    const [isOpenModalShare, setIsOpenModalShare] = useState<boolean>(false);

    useBlockScroll(isOpenModal || isOpenModalShare, false);

    const { referenciaContenido, posts = [] } = useJobRecommendationTips();

    const [, , recommendation, aboutUs, mentoryTip, protectmeTip] =
        referenciaContenido;

    const getData = async () => {
        const jobArea = jobDetails.area;
        if (jobArea) {
            let idArea;
            let idCity;
            let idRegion;
            let idWorkMode;

            await getJobs("size=1")
                .then((response) => {
                    const { area, city, region, workMode } = response.data.data
                        .optionFilters as JobsFilters;

                    idArea = area?.find(
                        (item) => item?.label === jobArea
                    )?.value;

                    idCity = city?.find(
                        (item) => item?.label === jobDetails?.city
                    )?.value;

                    idRegion = region?.find(
                        (item) => item?.label === jobDetails?.region
                    )?.value;

                    idWorkMode = workMode?.find(
                        (item) => item?.label === jobDetails?.workMode
                    )?.value;
                })
                .catch((err) => console.log(err));

            const params = new URLSearchParams({
                area: idArea,
                city: idCity,
                region: idRegion,
                workMode: idWorkMode,
                size: String(similarJobsQuantity),
            })?.toString();

            await getJobs(`${params}`)
                .then((response) => {
                    const content = (response?.data?.data?.vacantes?.content ||
                        []) as JobDetails[];

                    // excluir la vacante actual
                    const jobsToSave = content.filter(
                        (job) =>
                            job.referenceNumber !== jobDetails.referenceNumber
                    );
                    setSimilarPositions(jobsToSave);
                })
                .catch((err) => {
                    console.log("Error get jobs: " + err);
                });
        }
    };

    useEffect(() => {
        getData();
    }, [jobDetails]);

    const closeModal = () => {
        setIsOpenModal((setShow) => !setShow);
    };

    const redirect = async () => {
        if (globalContext.gigyaContext.gigyaAccount) {
            isBrowser && window.open(jobDetails.url, "_blank");
        }
    };

    const handleClickPostulate = () => {
        if (!globalContext.gigyaContext.gigyaAccount) {
            const checkGuideCdc = localStorage.getItem("guideCdc");
            if (checkGuideCdc !== "true") {
                setIsOpenModal(true);
            } else {
                globalContext.gigyaContext.loginComfamaId?.();
            }
            localStorage.setItem("doRedirect", "true");
        } else {
            redirect();
        }
    };

    // Si el usuario no ha iniciado sesión, se abre la vacante en una nueva pestaña para postularse sin login
    const handleClickPostulateWithoutLogin = () => {
        isBrowser && window.open(jobDetails.url, "_blank");
    };

    useEffect(() => {
        const shouldRedirect = localStorage.getItem("doRedirect") === "true";

        if (globalContext.gigyaContext.gigyaAccount && shouldRedirect) {
            localStorage.removeItem("doRedirect");

            setTimeout(() => {
                redirect();
            }, 1000);
        }
    }, [globalContext.gigyaContext.gigyaAccount]);

    const shareURL = encodeURIComponent(
        `${
            isBrowser ? window.location.origin : ""
        }/servicio-de-empleo/personas/vacantes-en-antioquia/vacante/?id=${
            jobDetails.referenceNumber
        }`
    );

    const customBreadcrumbs = useMemo(() => {
        return [
            ...breadcrumbItems,
            {
                title: jobDetails.title,
            },
        ];
    }, [jobDetails]);

    const recommendedJobs =
        similarPositions?.slice(0, recommendedJobsQuantity) || [];

    const mayLikeJobs = isMobile ? similarPositions :  similarPositions?.slice(recommendedJobsQuantity) || [];

    // Se requiere tener el tamaño del header para calcular el margen superior de la pagina
    const topSeparation =  headerSize.height || FALLBACK_HEADER_HEIGHT

    return (
        <article
            className={`${MAIN_CONTAINER_CLASSNAME}`}
            style={{
                marginTop: `${topSeparation}px`,
            }}
        >
            {/* Navegation */}

            <section className={`${MAIN_CONTAINER_CLASSNAME}__nav`}>
                <div className="wrapper">
                    <div className={`${MAIN_CONTAINER_CLASSNAME}__breadcrumb`}>
                        <Breadcrumb
                            useCustomBreadcrumbs
                            content={""}
                            customBreadcrumbs={customBreadcrumbs}
                        />
                    </div>

                    <GoBackLink
                        className={`${MAIN_CONTAINER_CLASSNAME}__go-back`}
                        text="Regresar a vacantes"
                        link="/servicio-de-empleo/personas/vacantes-en-antioquia"
                    />
                </div>
            </section>

            {/* Content Grid */}
            <div className={`${MAIN_CONTAINER_CLASSNAME}__container`}>
                <div className="wrapper">
                    <section
                        className={`${MAIN_CONTAINER_CLASSNAME}__grid_container`}
                    >
                        <JobName
                            jobDetails={jobDetails}
                            onClickPostulate={handleClickPostulateWithoutLogin}
                            onClickShare={() => setIsOpenModalShare(true)}
                        />

                        <div className={`${MAIN_CONTAINER_CLASSNAME}__column`}>
                            <JobGeneralInfo
                                jobDetails={jobDetails}
                                onClickPostulate={handleClickPostulateWithoutLogin}
                            />

                            <JobRecommendations
                                recommendation={
                                    recommendation.descripcion.raw || ""
                                }
                                aboutUs={aboutUs.descripcion.raw || ""}
                                onClickPostulate={handleClickPostulateWithoutLogin}
                            />

                            <GoBackLink
                                className={`${MAIN_CONTAINER_CLASSNAME}__go-back`}
                                text="Regresar a vacantes"
                                link="/servicio-de-empleo/personas/vacantes-en-antioquia"
                            />
                        </div>

                        <div
                            className={`${MAIN_CONTAINER_CLASSNAME}__column suggestions `}
                        >
                            <JobSuggestions
                                mentoryText={mentoryTip?.descripcion?.raw || ""}
                                protectmeText={
                                    protectmeTip?.descripcion?.raw || ""
                                }
                                recommendedJobs={recommendedJobs}
                                onClickPostulate={handleClickPostulateWithoutLogin}
                            />
                        </div>
                    </section>
                </div>
            </div>

            {/* Carousels */}
            <section>
                <JobRecommendedJobsCarousel jobs={mayLikeJobs} />

                <JobTipsPostsCarousel posts={posts as any} />
            </section>

            {isOpenModal && (
                <ModalGuiaCdc showModal={isOpenModal} onClose={closeModal} />
            )}

            {isOpenModalShare && (
                <ShareModal
                    isOpen
                    link={shareURL}
                    onClose={() => setIsOpenModalShare(false)}
                />
            )}
        </article>
    );
};

export default JobDetailsScreen;
